/**
 * Detects systemic AudioContext.decodeAudioData failures (e.g. an unsupported
 * Ogg Opus encoding served by a sample CDN) that smplr swallows internally via
 * console.warn, so sample-based instruments can fall back to the modeled synth
 * instead of silently playing nothing.
 *
 * Safe for concurrent instrument loads sharing the same AudioContext: the
 * wrapper is installed once (never restored) and counters are diffed per
 * load window, so overlapping loads don't race or misattribute failures.
 */
interface DecodeStats {
  decoded: number;
  failed: number;
}

const installed = new WeakSet<AudioContext>();
const stats = new WeakMap<AudioContext, DecodeStats>();

function ensureGuard(ctx: AudioContext): DecodeStats {
  let s = stats.get(ctx);
  if (!s) {
    s = { decoded: 0, failed: 0 };
    stats.set(ctx, s);
  }
  if (!installed.has(ctx)) {
    installed.add(ctx);
    const original = ctx.decodeAudioData.bind(ctx);
    (ctx as any).decodeAudioData = (audioData: ArrayBuffer) => {
      return original(audioData).then(
        (buffer) => {
          s!.decoded++;
          return buffer;
        },
        (err) => {
          s!.failed++;
          throw err;
        }
      );
    };
  }
  return s;
}

/** Snapshot of decode stats for this context, to diff across a load window. */
export function snapshotDecodeStats(ctx: AudioContext): DecodeStats {
  const s = ensureGuard(ctx);
  return { decoded: s.decoded, failed: s.failed };
}

/** True if every decode attempt since `before` failed (zero successes). */
export function decodingFailedSince(ctx: AudioContext, before: DecodeStats): boolean {
  const s = ensureGuard(ctx);
  const decodedDuring = s.decoded - before.decoded;
  const failedDuring = s.failed - before.failed;
  return failedDuring > 0 && decodedDuring === 0;
}
